import { useEffect, useState } from "react"
import { supabase, logAuditAction } from "@/lib/supabase"
import { useToast } from "@/context/ToastContext"
import { Button } from "@/components/common/Button"
import { FormGroup, Input, Select } from "@/components/common/Modal"
import { Save } from "lucide-react"

interface PlatformSettings {
  subscription_price: number
  reactivation_fee: number
  min_payout: number
  grace_period_days: number
  pay_compression: boolean
}

const DEFAULTS: PlatformSettings = {
  subscription_price: 6.99,
  reactivation_fee:   9.99,
  min_payout:         25.00,
  grace_period_days:  30,
  pay_compression:    true,
}

export function SettingsPage() {
  const { toast } = useToast()
  const [settings, setSettings]   = useState<PlatformSettings>(DEFAULTS)
  const [saving, setSaving]       = useState(false)

  useEffect(() => {
    supabase.from("platform_settings").select("*").maybeSingle().then(({ data }) => {
      if (data) setSettings({ ...DEFAULTS, ...data })
    })
  }, [])

  const save = async () => {
    setSaving(true)
    try {
      const { error } = await supabase
        .from("platform_settings")
        .upsert({ id: 1, ...settings, updated_at: new Date().toISOString() })
      if (error) throw error
      await logAuditAction("settings_changed", null, "Platform settings updated")
      toast("Settings saved", "success")
    } catch (err: unknown) {
      toast(err instanceof Error ? err.message : "Save failed", "error")
    } finally { setSaving(false) }
  }

  const set = (key: keyof PlatformSettings, val: unknown) =>
    setSettings(s => ({ ...s, [key]: val }))

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Pricing */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-50">
          <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">💰 Pricing</h2>
        </div>
        <div className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <FormGroup label="Subscription Price ($)">
            <Input type="number" step="0.01" value={settings.subscription_price}
              onChange={e => set("subscription_price", parseFloat(e.target.value))} />
          </FormGroup>
          <FormGroup label="Reactivation Fee ($)">
            <Input type="number" step="0.01" value={settings.reactivation_fee}
              onChange={e => set("reactivation_fee", parseFloat(e.target.value))} />
          </FormGroup>
          <FormGroup label="Minimum Payout ($)">
            <Input type="number" step="0.01" value={settings.min_payout}
              onChange={e => set("min_payout", parseFloat(e.target.value))} />
          </FormGroup>
        </div>
      </div>

      {/* Grace & Compression */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-50">
          <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">⏰ Grace & Compression</h2>
        </div>
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormGroup label="Grace Period (Days)">
            <Input type="number" min="1" max="90" value={settings.grace_period_days}
              onChange={e => set("grace_period_days", parseInt(e.target.value))} />
          </FormGroup>
          <FormGroup label="Pay Compression">
            <Select value={settings.pay_compression ? "true" : "false"}
              onChange={e => set("pay_compression", e.target.value === "true")}>
              <option value="true">Enabled</option>
              <option value="false">Disabled</option>
            </Select>
          </FormGroup>
        </div>
      </div>

      <Button variant="primary" size="lg" icon={<Save size={16} />} loading={saving} onClick={save}>
        Save Settings
      </Button>
    </div>
  )
}